import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Leaf, Activity, Apple, Sparkles, ShieldCheck, CalendarDays } from "lucide-react";

export default function Landing() {
  return (
    <div className="min-h-screen gradient-hero">
      {/* Nav */}
      <header className="max-w-7xl mx-auto px-6 lg:px-8 py-6 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="w-10 h-10 rounded-2xl bg-gradient-to-br from-emerald-400 to-sky-400 flex items-center justify-center">
            <Leaf className="w-5 h-5 text-white" />
          </div>
          <span className="font-display font-bold text-xl text-slate-900">NutriGuide</span>
        </div>
        <div className="flex items-center gap-3">
          <Link to="/login">
            <Button variant="ghost" className="rounded-full text-slate-700" data-testid="nav-login">
              Sign in
            </Button>
          </Link>
          <Link to="/register">
            <Button className="rounded-full bg-emerald-600 hover:bg-emerald-700" data-testid="nav-get-started">
              Get started
            </Button>
          </Link>
        </div>
      </header>

      {/* Hero */}
      <section className="max-w-7xl mx-auto px-6 lg:px-8 pt-16 pb-24 grid lg:grid-cols-2 gap-12 items-center">
        <div className="fade-up">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-sky-100 text-sky-700 text-xs font-semibold mb-6">
            <Sparkles className="w-3.5 h-3.5" />
            Personalized to your conditions
          </div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-900 leading-tight">
            Foods that work
            <span className="block text-emerald-600">with your health.</span>
          </h1>
          <p className="mt-6 text-base lg:text-lg text-slate-600 leading-relaxed max-w-xl">
            Diabetes, glaucoma, osteoporosis, hypertension, kidney disease and more —
            tell us about you, and we&apos;ll surface the right foods, meal plans, and
            drug-food interactions.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Link to="/register">
              <Button size="lg" className="rounded-full bg-emerald-600 hover:bg-emerald-700 px-7 h-12" data-testid="hero-get-started">
                Build my plan
              </Button>
            </Link>
            <Link to="/login">
              <Button size="lg" variant="outline" className="rounded-full h-12 border-emerald-200 text-slate-700 hover:bg-emerald-50" data-testid="hero-login">
                I have an account
              </Button>
            </Link>
          </div>
          <div className="mt-8 flex items-center gap-6 text-sm text-slate-500">
            <span className="inline-flex items-center gap-1.5"><ShieldCheck className="w-4 h-4 text-emerald-600" /> Evidence-based</span>
            <span className="inline-flex items-center gap-1.5"><Activity className="w-4 h-4 text-sky-600" /> 20 conditions</span>
            <span className="inline-flex items-center gap-1.5"><Apple className="w-4 h-4 text-emerald-600" /> 2,000+ foods</span>
          </div>
        </div>

        <div className="relative fade-up delay-2">
          <div className="aspect-[5/4] rounded-3xl overflow-hidden border border-emerald-100 shadow-2xl shadow-emerald-100/40">
            <img
              src="https://images.pexels.com/photos/5386708/pexels-photo-5386708.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
              alt="Fresh ingredients on a marble surface"
              className="w-full h-full object-cover"
            />
          </div>
          <div className="absolute -bottom-6 -left-6 bg-white rounded-2xl shadow-lg border border-emerald-100 p-4 max-w-[240px]">
            <div className="text-xs text-emerald-700 font-semibold tracking-wider uppercase">Recommended</div>
            <div className="font-display font-semibold text-slate-900 mt-1">Wild Salmon</div>
            <div className="text-xs text-slate-500 mt-1">High omega-3 — heart & glaucoma friendly.</div>
          </div>
          <div className="absolute -top-6 -right-6 bg-white rounded-2xl shadow-lg border border-sky-100 p-4 max-w-[240px]">
            <div className="text-xs text-amber-600 font-semibold tracking-wider uppercase">Caution</div>
            <div className="font-display font-semibold text-slate-900 mt-1">Aged Cheddar</div>
            <div className="text-xs text-slate-500 mt-1">High sodium for hypertension.</div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="max-w-7xl mx-auto px-6 lg:px-8 pb-24 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
        {[
          { icon: Activity, title: "Personalized scoring", desc: "Every food rated for you across all your conditions." },
          { icon: CalendarDays, title: "AI meal plans", desc: "A daily plan tuned to your meds and goals." },
          { icon: ShieldCheck, title: "Drug-food alerts", desc: "Know which foods clash with your medications." },
          { icon: Apple, title: "Huge food library", desc: "Thousands of items, deeply tagged." },
        ].map((f, i) => (
          <div key={i} className={`bg-white rounded-2xl border border-emerald-100 p-6 fade-up delay-${i + 1}`}>
            <div className="w-10 h-10 rounded-xl bg-emerald-50 flex items-center justify-center mb-4">
              <f.icon className="w-5 h-5 text-emerald-600" />
            </div>
            <div className="font-display font-semibold text-slate-900">{f.title}</div>
            <div className="text-sm text-slate-500 mt-1.5">{f.desc}</div>
          </div>
        ))}
      </section>

      <footer className="max-w-7xl mx-auto px-6 lg:px-8 py-10 border-t border-emerald-100 text-sm text-slate-500 flex flex-wrap items-center justify-between gap-3">
        <span>© NutriGuide — general nutrition guidance, not medical advice.</span>
        <span>Talk to your clinician about specific dietary changes.</span>
      </footer>
    </div>
  );
}
